import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Box, 
  Typography, 
  Tabs,
  Tab,
  Paper,
  Alert,
  Snackbar,
  Button,
  Container,
  Grid,
  Avatar,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stack,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Info as InfoIcon,
  Payment as PaymentIcon,
  Settings as SettingsIcon,
  TuneRounded as TuneIcon,
  Save as SaveIcon,
  CheckCircle as CheckIcon
} from '@mui/icons-material';

// ✅ Import enhanced components
import { usePOSSettings } from './hooks/usePOSSettings';
import CartAddOnsManager from './components/CartAddOnsManager';
import CartMetadataManager from './components/CartMetadataManager';
import PaymentMethodsManager from './components/PaymentMethodsManager';
import DefaultSettingsManager from './components/DefaultSettingsManager';

// ✅ Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pos-config-tabpanel-${index}`}
      aria-labelledby={`pos-config-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ py: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const POSConfig = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // ✅ State management
  const [currentTab, setCurrentTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // ✅ POS Settings hook with all functionality
  const {
    settings,
    loading,
    error,
    hasChanges,
    updateSettings,
    saveSettings
  } = usePOSSettings();

  // ✅ Auth check
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/signin');
    }
  }, [navigate]);

  // ✅ Handlers
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // ✅ FIXED: Complete updateCartAddOn function
  const updateCartAddOn = async (addOnId, updates) => {
    try {
      console.log('🔄 Update cart add-on:', { addOnId, updates });
      
      if (!settings?.cartAddOns) {
        throw new Error('CartAddOns not found');
      }

      // ✅ Handle both array and object formats properly
      let cartAddOnsData = settings.cartAddOns;
      let updatedData;

      if (Array.isArray(cartAddOnsData)) {
        // Array format - find and update
        const addOnIndex = cartAddOnsData.findIndex(addOn => addOn.id === addOnId);
        if (addOnIndex === -1) {
          throw new Error(`Add-on with ID "${addOnId}" not found`);
        }

        updatedData = [...cartAddOnsData];
        updatedData[addOnIndex] = {
          ...updatedData[addOnIndex],
          ...updates
        };
      } else if (typeof cartAddOnsData === 'object') {
        // Object format - direct update
        if (!cartAddOnsData[addOnId]) {
          throw new Error(`Add-on with ID "${addOnId}" not found`);
        }

        updatedData = {
          ...cartAddOnsData,
          [addOnId]: {
            ...cartAddOnsData[addOnId],
            ...updates
          }
        };
      } else {
        throw new Error('Invalid cartAddOns format');
      }

      await updateSettings({
        ...settings,
        cartAddOns: updatedData
      });

      setSnackbar({
        open: true,
        message: t('posConfig.messages.addOnUpdated') || 'Add-on updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error updating cart add-on:', error);
      setSnackbar({
        open: true,
        message: t('posConfig.messages.addOnUpdateFailed') || `Failed to update add-on: ${error.message}`,
        severity: 'error'
      });
    }
  };

  // ✅ FIXED: Complete updateCartMetadata function
  const updateCartMetadata = async (fieldName, fieldConfig) => {
    try {
      console.log('🔄 Update cart metadata:', { fieldName, fieldConfig });
      
      const currentMetadata = { ...settings?.cartMetadata };
      currentMetadata[fieldName] = {
        ...currentMetadata[fieldName],
        ...fieldConfig
      };
      
      await updateSettings({ 
        ...settings,
        cartMetadata: currentMetadata 
      });
      
      setSnackbar({
        open: true,
        message: t('posConfig.messages.metadataUpdated') || 'Metadata updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error updating cart metadata:', error);
      setSnackbar({
        open: true,
        message: t('posConfig.messages.metadataUpdateFailed') || 'Failed to update metadata',
        severity: 'error'
      });
    }
  };

  // ✅ FIXED: Complete updatePaymentMethod function with proper deletion support
  const updatePaymentMethod = async (methodId, updates, isBulkUpdate = false) => {
  try {
    console.log('🔄 Update payment method:', { methodId, updates, isBulkUpdate });
    
    let updatedMethods;

    if (methodId === '__ALL__' || isBulkUpdate) {
      // ✅ Bulk update - replace entire payment methods object
      updatedMethods = typeof updates === 'object' ? updates : {};
      console.log('🔄 Bulk update payment methods. New count:', Object.keys(updatedMethods).length);
      
      // ✅ Log the specific change
      if (isBulkUpdate && Object.keys(settings?.paymentMethods || {}).length > Object.keys(updatedMethods).length) {
        const deletedMethods = Object.keys(settings?.paymentMethods || {}).filter(
          key => !Object.prototype.hasOwnProperty.call(updatedMethods, key)
        );
        console.log('🗑️ Methods being deleted:', deletedMethods);
      }
    } else {
      // ✅ Single method update
      const currentMethods = { ...settings?.paymentMethods };
      
      if (methodId === 'calculate_change' || methodId === 'calculateChange') {
        // Handle calculate change special case
        updatedMethods = {
          ...currentMethods,
          calculate_change: {
            id: 'calculate_change',
            name: 'Calculate Change',
            icon: 'Calculate',
            ...currentMethods.calculate_change,
            ...updates
          }
        };
        
        // Remove any duplicate calculateChange entries
        if (updatedMethods.calculateChange) {
          delete updatedMethods.calculateChange;
        }
      } else {
        // Normal update
        updatedMethods = {
          ...currentMethods,
          [methodId]: {
            ...currentMethods[methodId],
            ...updates
          }
        };
      }
    }
    
    // ✅ CRITICAL: Create completely new settings object to force re-render
    const newSettings = {
      ...settings,
      paymentMethods: { ...updatedMethods }, // ✅ Create new object reference
      _lastUpdated: Date.now() // ✅ Add timestamp to force updates
    };
    
    console.log('🔄 Updating settings with new payment methods:', {
      oldCount: Object.keys(settings?.paymentMethods || {}).length,
      newCount: Object.keys(updatedMethods).length,
      newMethods: Object.keys(updatedMethods)
    });
    
    await updateSettings(newSettings);
    
    setSnackbar({
      open: true,
      message: isBulkUpdate ? 
        (t('posConfig.messages.paymentMethodsUpdated') || 'Payment methods updated successfully') :
        (t('posConfig.messages.paymentMethodUpdated') || 'Payment method updated successfully'),
      severity: 'success'
    });
    
    console.log('✅ Payment method update completed');
    
  } catch (error) {
    console.error('❌ Error updating payment method:', error);
    setSnackbar({
      open: true,
      message: t('posConfig.messages.paymentMethodUpdateFailed') || 'Failed to update payment method',
      severity: 'error'
    });
  }
};

  // ✅ FIXED: Complete handleSaveSettings function
  const handleSaveSettings = async () => {
    try {
      console.log('💾 Manual save triggered');
      await saveSettings();
      setSnackbar({
        open: true,
        message: t('posConfig.messages.settingsSaved') || 'All settings saved successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error saving settings:', error);
      setSnackbar({
        open: true,
        message: t('posConfig.messages.settingsSaveFailed') || 'Failed to save settings',
        severity: 'error'
      });
    }
  };

  // ✅ FIXED: Add onSave callback for CartAddOnsManager  
  const handleSaveCartAddOns = async (updatedCartAddOns) => {
    try {
      console.log('💾 Save cart add-ons:', updatedCartAddOns);
      
      await updateSettings({
        ...settings,
        ...updatedCartAddOns
      });
      
      setSnackbar({
        open: true,
        message: t('posConfig.messages.cartAddOnsSaved') || 'Cart add-ons saved successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('❌ Error saving cart add-ons:', error);
      setSnackbar({
        open: true,
        message: t('posConfig.messages.cartAddOnsSaveFailed') || 'Failed to save cart add-ons',
        severity: 'error'
      });
    }
  };

  // ✅ Loading state
  if (loading) {
    return (
      <Box sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        gap: 2,
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <Paper sx={{
          p: 4,
          textAlign: 'center',
          background: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: 3,
          minWidth: 300
        }}>
          <TuneIcon sx={{ fontSize: 48, color: '#667eea', mb: 2 }} />
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            {t('posConfig.loading.title') || 'Loading POS Configuration'}
          </Typography>
          <CircularProgress size={40} sx={{ color: '#667eea' }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {t('posConfig.loading.message') || 'Please wait while we load your settings...'}
          </Typography>
        </Paper>
      </Box>
    );
  }

  // ✅ Error state
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{
          p: 6,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
          border: '1px solid #ef444430',
          borderRadius: 4
        }}>
          <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}>
            {t('posConfig.error.title') || 'Configuration Load Failed'}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
            {error}
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              }}
            >
              {t('common.retry') || 'Retry'}
            </Button>
            <Button
              variant="outlined"
              onClick={() => navigate('/dashboard')}
            >
              {t('posConfig.error.backToDashboard') || 'Back to Dashboard'}
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  // ✅ No settings state
  if (!settings) {
    return (
      <Container maxWidth="md" sx={{ py: 8 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {t('posConfig.noSettings.title') || 'No Configuration Found'}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t('posConfig.noSettings.message') || 'Please wait while we load your settings...'}
          </Typography>
          <Button
            variant="contained"
            onClick={() => window.location.reload()}
          >
            {t('common.refresh') || 'Refresh'}
          </Button>
        </Paper>
      </Container>
    );
  }

  // ✅ ENHANCED: Complete tab configuration with all functionality
  const tabs = [
    {
      label: t('posConfig.tabs.cartAddOns') || 'Cart Add-ons',
      icon: <AddIcon />,
      component: CartAddOnsManager,
      description: t('posConfig.tabs.cartAddOnsDesc') || 'Configure service charges, taxes, and discounts',
      color: '#667eea',
      props: {
        settings,
        updateCartAddOn,
        onSave: handleSaveCartAddOns, // ✅ Add onSave callback
        isMobile,
        isTablet
      }
    },
    {
      label: t('posConfig.tabs.cartMetadata') || 'Cart Metadata',
      icon: <InfoIcon />,
      component: CartMetadataManager,
      description: t('posConfig.tabs.cartMetadataDesc') || 'Manage order information fields',
      color: '#10b981',
      props: {
        settings,
        updateCartMetadata // ✅ This enables/disables metadata fields
      }
    },
    {
      label: t('posConfig.tabs.paymentMethods') || 'Payment Methods',
      icon: <PaymentIcon />,
      component: PaymentMethodsManager,
      description: t('posConfig.tabs.paymentMethodsDesc') || 'Setup payment processing options',
      color: '#f59e0b',
      props: {
        settings,
        updatePaymentMethod // ✅ This handles adding new payment methods
      }
    },
    {
      label: t('posConfig.tabs.defaultSettings') || 'Default Settings',
      icon: <SettingsIcon />,
      component: DefaultSettingsManager,
      description: t('posConfig.tabs.defaultSettingsDesc') || 'Configure system defaults',
      color: '#8b5cf6',
      props: {
        settings,
        updateSettings: async (updates) => {
          try {
            await updateSettings(updates);
            setSnackbar({
              open: true,
              message: t('posConfig.messages.settingsUpdated') || 'Settings updated successfully',
              severity: 'success'
            });
          } catch (updateError) { // ✅ Changed name to be more specific
            console.error('❌ Error updating settings:', updateError); // ✅ Now using the error
            setSnackbar({
              open: true,
              message: t('posConfig.messages.settingsUpdateFailed') || 'Failed to update settings',
              severity: 'error'
            });
          }
        }
      }
    }
  ];

  console.log('✅ POSConfig rendering with settings:', Object.keys(settings));

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', py: 2 }}>
      <Container maxWidth="xl">
        {/* ✅ Header with action buttons */}
        <Paper sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          p: 4,
          borderRadius: 4,
          mb: 3,
          position: 'relative',
          overflow: 'hidden'
        }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <Avatar sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  width: { xs: 56, md: 72 },
                  height: { xs: 56, md: 72 }
                }}>
                  <TuneIcon sx={{ fontSize: { xs: 28, md: 40 }, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant="h3" sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.75rem', md: '2.5rem' },
                    mb: 1
                  }}>
                    {t('posConfig.header.title') || 'POS Configuration'}
                  </Typography>
                  <Typography variant="h6" sx={{
                    opacity: 0.9,
                    fontSize: { xs: '1rem', md: '1.25rem' }
                  }}>
                    {t('posConfig.header.subtitle') || 'Configure and customize your point of sale system'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Stack direction="row" spacing={1} justifyContent={{ xs: 'center', md: 'flex-end' }}>
                {hasChanges && (
                  <Chip
                    icon={<CheckIcon />}
                    label={t('posConfig.unsavedChanges') || 'Unsaved Changes'}
                    color="warning"
                    sx={{ bgcolor: 'rgba(255,193,7,0.2)', color: 'white' }}
                  />
                )}
                <Button
                  variant="outlined"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveSettings}
                  disabled={!hasChanges}
                  sx={{
                    borderColor: 'rgba(255,255,255,0.3)',
                    color: 'white',
                    '&:hover': {
                      borderColor: 'rgba(255,255,255,0.5)',
                      background: 'rgba(255,255,255,0.1)'
                    }
                  }}
                >
                  {isMobile ? (t('common.save') || 'Save') : (t('posConfig.saveChanges') || 'Save Changes')}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Paper>

        {/* ✅ Main Content with tabs */}
        <Paper sx={{ borderRadius: 4, overflow: 'hidden', background: 'white' }}>
          {/* ✅ Tabs with descriptions */}
          <Box sx={{ borderBottom: '1px solid #e2e8f0' }}>
            <Tabs
              value={currentTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: { xs: '0.875rem', md: '1rem' },
                  minHeight: { xs: 64, md: 72 },
                  '&.Mui-selected': {
                    color: '#667eea'
                  }
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#667eea',
                  height: 3
                }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  icon={
                    <Avatar sx={{
                      bgcolor: currentTab === index ? tab.color : 'rgba(0,0,0,0.04)',
                      color: currentTab === index ? 'white' : 'rgba(0,0,0,0.6)',
                      width: { xs: 32, md: 40 },
                      height: { xs: 32, md: 40 },
                      mb: 1
                    }}>
                      {React.cloneElement(tab.icon, {
                        sx: { fontSize: { xs: 16, md: 20 } }
                      })}
                    </Avatar>
                  }
                  label={
                    <Box>
                      <Typography variant="subtitle2" sx={{
                        fontWeight: currentTab === index ? 700 : 600,
                        fontSize: { xs: '0.75rem', md: '0.875rem' }
                      }}>
                        {tab.label}
                      </Typography>
                      {!isMobile && (
                        <Typography variant="caption" sx={{
                          color: currentTab === index ? tab.color : 'text.secondary',
                          fontSize: '0.75rem'
                        }}>
                          {tab.description}
                        </Typography>
                      )}
                    </Box>
                  }
                  iconPosition="top"
                />
              ))}
            </Tabs>
          </Box>

          {/* ✅ Tab Content with components */}
          <Box sx={{ minHeight: { xs: '50vh', md: '60vh' } }}>
            {tabs.map((tab, index) => {
              const Component = tab.component;
              return (
                <TabPanel key={index} value={currentTab} index={index}>
                  <Box sx={{ p: { xs: 2, md: 3 } }}>
                    <Component {...tab.props} />
                  </Box>
                </TabPanel>
              );
            })}
          </Box>
        </Paper>

        {/* ✅ Footer with status */}
        <Paper sx={{ 
          mt: 3, 
          p: 3, 
          textAlign: 'center', 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 3
        }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            {t('posConfig.footer.title') || '🚀 POS Configuration Ready!'}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            {hasChanges ? 
              (t('posConfig.footer.unsavedMessage') || 'You have unsaved changes. Click "Save Changes" to apply them.') :
              (t('posConfig.footer.savedMessage') || 'All settings are automatically saved and synced across devices')
            }
          </Typography>
        </Paper>
      </Container>

      {/* ✅ Snackbar notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ 
            width: '100%',
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default POSConfig;